import { makeAutoObservable } from "mobx";
import type { RgSite } from "./RgCorePackage";
import type { V3 } from "@project-rouge/rg-core";
import { DefaultSite, RgCreate } from "@project-rouge/rg-core";
import { Zone } from "./Zone";
import type { World } from "./World";
import { GetPolygonCentroid } from "src/utils/GetPolygonCentroid";
import { Dataset } from "src/constants/Dataset";
import type { ProjectScenarioSiteHttpPayloadForCreation } from "src/service/project/resource/project-scenario-site";
import type { GeoPoint } from "./GeoPoint";
import { V3ToV2 } from "src/utils/V3ToV2";
import clip from "polygon-clipping";
import type { Building } from "./Building";
import { MathSum } from "src/utils/MathSum";
interface SiteConfig {
  rgSite?: RgSite;
}
export class Site {
  children: Zone[] = [];
  id: string;
  parent: World | undefined;
  constructor(config?: SiteConfig) {
    this.id = config?.rgSite?.uuid ?? crypto.randomUUID();
    config?.rgSite?.children.forEach((rgZone) => this.addZone(new Zone({ rgZone })));
    makeAutoObservable(this);
  }
  get world(): World | undefined {
    return this.parent;
  }
  get buildings(): Building[] {
    return this.zones.flatMap((zone) => zone.buildings);
  }
  get siteAreaHectares() {
    return MathSum(this.zones.map((zone) => zone.siteAreaHectares));
  }

  get outerRing(): V3[] {
    const forUnion = this.zones.map((zone) => [zone.outerRing.map((v) => V3ToV2(v))]);
    const rest = clip.union(forUnion);
    if (rest.length === 0) return [];
    if (rest.length > 1) throw new Error("Union of polygons should only return one polygon");
    const [multipolygon] = rest;
    const [outerRingV2] = multipolygon;
    return outerRingV2.map((v) => {
      return [v[0], 0, v[1]] as V3;
    });
  }
  get geoOuterRing(): GeoPoint[] {
    if (!this.world) return [];
    return this.zones.at(0)?.geoOuterRing ?? [];
  }
  get centroid() {
    return GetPolygonCentroid(this.outerRing);
  }
  get zones(): Zone[] {
    return this.children;
  }
  get rgSite(): RgSite {
    // @todo - use dataset from world or let user choose
    const dataset = Dataset;
    const rgSite = RgCreate<RgSite>(DefaultSite, dataset.rvData);
    rgSite.uuid = this.id;
    rgSite.children = this.zones.map((zone) => {
      const rgZone = zone.rgZone;
      rgZone.parent = rgSite;
      return rgZone;
    });
    return rgSite;
  }
  /** DONT use it directly. use `world.addZone` */
  addZone(zone: Zone): void {
    if (this.children.includes(zone)) return;
    zone.parent = this;
    this.children.push(zone);
  }
  removeZone(zone: Zone): void {
    const children = this.children.filter((z) => z !== zone);
    if (children.length === this.children.length) return;
    zone.parent = undefined;
    this.children = children;
  }

  hasZoneOverlap(zone: Zone) {
    return this.zones.some((z) => z.hasZoneOverlap(zone));
  }

  get hasZone(): boolean {
    return this.children.length > 0;
  }

  get beSite(): ProjectScenarioSiteHttpPayloadForCreation {
    return {
      id: this.id,
      region: {
        perimeter: this.geoOuterRing,
      },
      zones: this.zones.map((zone) => zone.beZone),
    };
  }
}
